import React, { useState } from 'react'
import SearchFilterIcon from '../../assets/icons/search-filter'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import TablePagination from '../../components/Table/Pagination'
import ResidentService from '../../api/resident-service'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import profileIcon from '../../assets/icons/profile-icon.svg'
import SearchIcon from '../../assets/icons/search.svg'
import { ResidentDTO } from '../../interfaces/resident'
import DisableResidentModal from '../../components/Resident/DisableResident'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'
import Empty from '../../assets/icons/Empty'
import { useDebounce } from '../../hooks/useDebounce'

const residentStatuses = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Disabled', value: 'inactive' },
]

const tableHeadData = [
  'Name of Residents',
  'Phone number',
  'Email Address',
  'Flat Address',
  'Status',
  'Validity',
  '',
]

interface ViewResidentsProps {
  setNumOfResidents: (num: number) => void
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  onViewDetails: (resident: ResidentDTO) => void
}

export const getUserFullName = (user?: {
  firstName?: string
  lastName?: string
  name?: string
}) => {
  if (!user) return 'Name Unavailable'

  const { firstName, lastName, name } = user
  return firstName && lastName ? `${firstName} ${lastName}` : name || 'Name Unavailable'
}

const ViewResidents = ({
  setNumOfResidents,
  handleViewSelect,
  onViewDetails,
}: ViewResidentsProps) => {
  const [statusFilter, setStatusFilter] = React.useState({
    label: 'All',
    value: 'all',
  })
  const [currentPage, setCurrentPage] = React.useState(1)
  const [openDisableModal, setOpenDisableModal] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [resident, setResident] = React.useState<ResidentDTO>()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 400)

  const handleViewDetails = (residentData: ResidentDTO) => {
    onViewDetails(residentData)
    handleViewSelect('viewDetails', { id: residentData.id })
  }

  const { data, isLoading, mutate } = ResidentService.useGetResidentList({
    page: currentPage,
    search: debouncedSearch,
  })

  React.useEffect(() => {
    if (data?.totalResults !== undefined) {
      setNumOfResidents(data.totalResults)
    }
  }, [data?.totalResults, setNumOfResidents])

  const handleStatusSelect = (status: { value: string; label: string }) => {
    setStatusFilter(status)
  }

  const handleDisableClick = (data: ResidentDTO) => {
    setResident(data)
    setOpenDisableModal(true)
  }

  const filteredResults = React.useMemo(() => {
    if (!data?.results) return []

    switch (statusFilter.value) {
      case 'all':
        return data.results.filter((residentData) =>
          getUserFullName(residentData.resident)
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      case 'active':
        return data.results.filter((residentData) => residentData.isActive)
      case 'inactive':
        return data.results.filter((residentData) => !residentData.isActive)
      default:
        return data.results
    }
  }, [data, statusFilter, searchQuery])

  return (
    <div className="mt-12 grid gap-y-10">
      {isLoading ? (
        <ViewStaffsSkeletonLoader />
      ) : (
        <>
          <div className="search-filter-bar flex w-full justify-between gap-2.5">
            <form
              onSubmit={(e) => {
                e.preventDefault()
              }}
              className="w-full flex bg-gray-100 rounded-lg items-center px-2"
            >
              <div className="Relative flex items-center w-full">
                <img src={SearchIcon} alt="Search Icon" />
                <input
                  placeholder=" Search Resident"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="py-2.5 w-full bg-transparent h-full"
                ></input>
              </div>
            </form>
            <div className="w-fit p-2.5 flex gap-5 whitespace-nowrap relative inline-block group w-44 justify-end font-semibold">
              <p>{statusFilter.label}</p>
              <div className="bg-gray-100 w-8 h-8 flex items-center justify-center rounded-lg">
                <SearchFilterIcon />
              </div>
              <div className="absolute z-10 top-12 right-0 invisible group-hover:visible shadow cursor-pointer w-40 rounded-xl overflow-hidden shadow-[4px_4px_20px_4px_rgba(33,37,41,0.10)] border bg-white">
                {residentStatuses.map((status) => (
                  <p
                    onClick={() => handleStatusSelect(status)}
                    className={`hover:bg-gray-200 p-3 ${
                      status.value === statusFilter.value ? 'text-[#069E2D]' : ''
                    } border-t`}
                    key={status.value}
                  >
                    {status.label}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <div className="w-full -mx-2 sm:mx-0 px-2 sm:px-0">
            <CustomTable>
              <CustomTableHead dataList={tableHeadData} />
              {!isLoading && filteredResults?.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={tableHeadData.length}>
                      <div className="flex flex-col items-center justify-center h-[300px] w-full gap-4">
                        <Empty />
                        <p className="text-sm font-semibold">No results found</p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <CustomTableBody>
                  {filteredResults?.map((residentData) => {
                    const resident = residentData.resident
                    const status = residentData.isActive ? 'Active' : 'Disabled'
                    return (
                      <CustomTableRow key={resident.id}>
                        <CustomTableData
                          dataLabel="Name of Residents"
                          text={getUserFullName(resident)}
                          imageUrl={resident.imageUrl || profileIcon}
                        />
                        <CustomTableData
                          dataLabel="Phone Number"
                          text={resident?.phone || 'NIL'}
                          className="px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base"
                        />
                        <CustomTableData
                          dataLabel="Email Address"
                          text={resident?.email || 'NIL'}
                          className="px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base"
                        />
                        <CustomTableData
                          dataLabel="Flat Address"
                          text={resident?.address || 'NIl'}
                          className="hidden sm:table-cell px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base break-words max-w-[200px] lg:max-w-none"
                        />
                        <CustomTableData
                          dataLabel="Status"
                          text={status}
                          className={`px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base ${residentData.isActive ? 'text-green-500' : 'text-red-500'}`}
                        />
                        <CustomTableData
                          dataLabel="Validity"
                          text={residentData.isActive ? 'Valid' : 'Invalid'}
                          className={`px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base ${residentData.isActive ? 'text-green-500' : 'text-red-500'}`}
                        />
                        <CustomTableDataCTA dataLabel="More" text="...">
                          <p
                            onClick={() => handleDisableClick(residentData)}
                            className="hover:bg-gray-200 p-3 border-t"
                          >
                            {residentData.isActive
                              ? 'Disable Resident'
                              : 'Enable Resident'}
                          </p>
                          {/* <p className="hover:bg-gray-200 p-3 border-t text-red-500">
                          Remove resident
                        </p> */}
                          <p
                            onClick={() => handleViewDetails(residentData)}
                            className="hover:bg-gray-200 p-3 border-t"
                          >
                            View details
                          </p>
                        </CustomTableDataCTA>
                      </CustomTableRow>
                    )
                  })}
                </CustomTableBody>
              )}
            </CustomTable>
          </div>
          {data?.results && data?.results.length > 0 && (
            <TablePagination
              totalPages={data?.totalPages}
              currPage={data?.page}
              setCurrentPage={setCurrentPage}
            />
          )}

          {openDisableModal && (
            <DisableResidentModal
              open={openDisableModal}
              setIsOpen={setOpenDisableModal}
              loading={loading}
              setLoading={setLoading}
              residentId={resident?.id}
              initialStatus={resident?.isActive}
              reload={mutate}
            />
          )}
        </>
      )}
    </div>
  )
}

export default ViewResidents
